<template>
  <div style="position: relative;">
    <div>
      <div class="d-flex justify-content-between" style="margin-bottom: 1.70rem;">
        <div class="d-flex align-items-center" style="gap: 2.34rem;">
          <div class="search2">
            <div class="search2--icon"><i class="iconoir-search"></i></div>
            <input ref="nameFilter" v-model="nameFilter" class="search2--input" type="text" :placeholder="$t('common.search')" />
          </div>
        </div>

        <div class="d-flex align-items-center float-right ml-4">
          <div v-if="activeTab === 'ORGANIZATION_AUDITS'" class="d-flex align-items-center" @click="toggleRightPanelAudit">
            <div class="new-task-text">{{ $t("audit-control.audit.new") }}</div>
            <div class="plus-button" style="margin-right: 1.56rem;"></div>
          </div>

          <div v-if="activeTab === 'ORGANIZATION_CONTROLS'" class="d-flex align-items-center" @click="toggleRightPanelControl">
            <div class="new-task-text">{{ $t("audit-control.control.new") }}</div>
            <div class="plus-button" style="margin-right: 1.56rem;"></div>
          </div>
        </div>
      </div>

      <!-- Right Panel for Audit -->
      <right-panel id="sidebar-new-audit" width="600px" @hidden="rightPanelClosedAudit" :text="$t('audit-control.audit.new')">
        <div class="pl-5 pr-5">
          <OrganizationAuditForm @onSave="paginateAudit" />
        </div>
      </right-panel>

      <!-- Right Panel for Control -->
      <right-panel id="sidebar-new-control" width="1000px" @hidden="rightPanelClosedControl" :text="$t('audit-control.control.new')">
        <div class="pl-5 pr-5">
          <OrganizationControlForm @onSave="paginateControl" :allowedClass="'AUDIT_ACTIVITY'" :lockedClass="true" />
        </div>
      </right-panel>

      <nav class="tab-menu">
        <div
          :class="{ active: activeTab === 'ORGANIZATION_AUDITS' }"
          @click="
            activeTab = 'ORGANIZATION_AUDITS';
            if (!activeSubTab) activeSubTab = 'IN_PROGRESS';
          "
        >
          {{ $t("firm.audit.list-title") }}
        </div>
        <div :class="{ active: activeTab === 'ORGANIZATION_CONTROLS' }" @click="activeTab = 'ORGANIZATION_CONTROLS'">
          {{ $t("audit-control.list.all") }}
        </div>
      </nav>

      <div class="mt-4">
        <!-- Sub-tabs for Organization Audits -->
        <div class="mb-4" v-if="activeTab === 'ORGANIZATION_AUDITS'">
          <nav class="tab-menu">
            <div :class="{ active: activeSubTab === 'IN_PROGRESS' }" @click="activeSubTab = 'IN_PROGRESS'">
              {{ $t("audit-control.audit.status.in_progress") }}
            </div>
            <div :class="{ active: activeSubTab === 'COMPLETED' }" @click="activeSubTab = 'COMPLETED'">
              {{ $t("audit-control.audit.status.completed") }}
            </div>
          </nav>
        </div>

        <!-- Audits Content (Shared for IN_PROGRESS and COMPLETED) -->
        <div v-if="activeTab === 'ORGANIZATION_AUDITS'">
          <content-loading v-if="isLoadingAudit" />
          <div v-else>
            <OrganizationAuditList :pagination="paginationAudit" :isLoading="isLoadingAudit" @edit="editAudit" />
            <!-- @delete="deleteAudit"  -->

            <empty v-if="!isLoadingAudit && (!paginationAudit || paginationAudit.docs.length == 0)">
              <h5 class="text-center">
                {{ $t("common.no-data") }}
              </h5>
            </empty>

            <div v-if="paginationAudit && paginationAudit.docs.length > 0" class="row mt-4">
              <div class="col">
                <ul class="pagination pagination-rounded mb-0">
                  <b-pagination v-model="requestAudit.options.page" :total-rows="paginationAudit.totalDocs" :per-page="paginationAudit.limit" limit="10" size="sm"></b-pagination>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'ORGANIZATION_CONTROLS'">
          <div class="d-flex flex-column">
            <div class="d-flex justify-content-end mb-3">
              <b-button variant="light" size="sm" class="text-info font-weight-bold" @click="showFilters = !showFilters">
                <i class="bx bx-filter"></i> {{ $t("common.filter") }}
              </b-button>
            </div>

            <div class="d-flex">
              <div v-if="showFilters" class="mr-3 border-right pr-3" style="width: 320px; flex-shrink: 0;">
                <AuditControlFilterSidebar />
              </div>

              <div class="flex-grow-1" style="min-width: 0;">
                <content-loading v-if="isLoadingControl" />
                <div v-else>
                  <OrganizationControlList :pagination="paginationControl" :isLoading="isLoadingControl" @edit="editControl" @delete="deleteControl" />

                  <empty v-if="!isLoadingControl && (!paginationControl || paginationControl.docs.length == 0)">
                    <h5 class="text-center">
                      {{ $t("common.no-data") }}
                    </h5>
                  </empty>

                  <div v-if="paginationControl && paginationControl.docs.length > 0" class="row mt-4">
                    <div class="col">
                      <ul class="pagination pagination-rounded mb-0">
                        <b-pagination
                          v-model="requestControl.options.page"
                          :total-rows="paginationControl.totalDocs"
                          :per-page="paginationControl.limit"
                          limit="10"
                          size="sm"
                        ></b-pagination>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import OrganizationControlForm from "../control/organization-control-form.vue";
import OrganizationControlList from "../control/organization-control-list.vue";
import OrganizationAuditForm from "./organization-audit-form.vue";
import OrganizationAuditList from "./organization-audit-list.vue";
import AuditControlFilterSidebar from "./audit-control-filter-sidebar.vue";

export default {
  components: { OrganizationAuditList, OrganizationAuditForm, OrganizationControlList, OrganizationControlForm, AuditControlFilterSidebar },
  data() {
    return {
      nameFilter: undefined,
      requestAudit: {
        options: { page: 1, limit: 15, sort: { createdAt: "desc" } },
        query: { status: { value: "IN_PROGRESS", isObject: true } },
      },
      requestControl: {
        options: { page: 1, limit: 15, sort: { createdAt: "desc" } },
        query: { class: { value: "AUDIT_ACTIVITY", isObject: true } },
      },
    };
  },

  computed: {
    paginationAudit() {
      return this.$store.getters["organizationAudit/pagination"];
    },
    isLoadingAudit() {
      return this.$store.getters["organizationAudit/isLoading"];
    },
    paginationControl() {
      return this.$store.getters["organizationControl/pagination"];
    },
    isLoadingControl() {
      return this.$store.getters["organizationControl/isLoading"];
    },
    activeTab: {
      get() {
        return this.$store.getters["organizationAudit/activeTab"];
      },
      set(v) {
        this.$store.commit("organizationAudit/activeTab", v);
      },
    },
    activeSubTab: {
      get() {
        return this.$store.getters["organizationAudit/activeSubTab"];
      },
      set(v) {
        this.$store.commit("organizationAudit/activeSubTab", v);
      },
    },
    controlFilters() {
      return this.$store.getters["organizationControl/controlFilters"] || {};
    },
    showFilters: {
      get() {
        return this.$store.getters["organizationControl/showFilterSidebar"];
      },
      set(v) {
        this.$store.commit("organizationControl/showFilterSidebar", v);
      },
    },
  },

  watch: {
    nameFilter(n) {
      if (this.activeTab == "ORGANIZATION_CONTROLS") {
        this.debounceMixin(() => this.updateFilter("name", n), "filter");
        return;
      }
      if (n) {
        this.requestAudit.query = { ...this.requestAudit.query, name: { value: n } };
      } else {
        const newQuery = { ...this.requestAudit.query };
        delete newQuery.name;
        this.requestAudit.query = newQuery;
      }
      this.requestAudit.options.page = 1;

      this.debounceMixin(() => this.paginateAudit(), "audit");
    },
    activeTab() {
      this.nameFilter = undefined;
      if (this.activeTab === "ORGANIZATION_AUDITS") {
        this.updateAuditQuery();
      }
    },
    controlFilters: {
      deep: true,
      handler() {
        this.requestControl.options.page = 1;
        this.debounceMixin(() => this.paginateControl(), "control");
      },
    },
    activeSubTab() {
      if (this.activeTab === "ORGANIZATION_AUDITS") {
        this.updateAuditQuery();
      }
    },
    requestAudit: {
      deep: true,
      handler() {
        this.debounceMixin(() => this.paginateAudit(), "audit");
      },
    },
    requestControl: {
      deep: true,
      handler() {
        this.debounceMixin(() => this.paginateControl(), "control");
      },
    },
  },

  mounted() {
    this.updateAuditQuery();
    // Initial fetch handled by watch immediate or manual call if removed from watch
    this.paginateControl();
  },

  methods: {
    updateFilter(key, val) {
      const newFilters = { ...this.controlFilters, [key]: val };
      this.$store.commit("organizationControl/controlFilters", newFilters);
    },
    updateAuditQuery() {
      // If we are in controls tab, do nothing for audits, or handle accordingly.
      // But activeTab / activeSubTab check above ensures we only call this when in audit tabs.
      const status = this.activeSubTab;
      this.requestAudit.query.status = { value: status, isObject: true };
      this.requestAudit.options.page = 1;
    },

    // Audit Methods
    toggleRightPanelAudit() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-new-audit");
    },
    rightPanelClosedAudit() {
      this.$store.commit("organizationAudit/editedEntity", undefined);
    },
    async paginateAudit() {
      await this.$store.dispatch("organizationAudit/paginate", this.requestAudit);
    },
    editAudit(item) {
      this.$store.commit("organizationAudit/editedEntity", item);
      this.toggleRightPanelAudit();
    },
    async deleteAudit(id) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`organizationAudit/delete`, id);
        this.paginateAudit();
        this.$toast.success(this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },

    // Control Methods
    toggleRightPanelControl() {
      this.$root.$emit("bv::toggle::collapse", "sidebar-new-control");
    },
    rightPanelClosedControl() {
      this.$store.commit("organizationControl/editedEntity", undefined);
    },
    async paginateControl() {
      this.$store.commit("organizationControl/isLoading", true);
      try {
        const response = await this.$store.dispatch("organizationControl/fetchFilteredControls", {
          query: this.requestControl.query,
          options: this.requestControl.options,
        });
        this.$store.commit("organizationControl/pagination", response.data);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      } finally {
        this.$store.commit("organizationControl/isLoading", false);
      }
    },
    editControl(item) {
      this.$store.commit("organizationControl/editedEntity", item);
      this.$router.push(`/audit-control/audit/control/${item._id}/detail?from=audit-view`);
    },
    async deleteControl(id) {
      const ok = await this.confirm(this.$t("alert.approve"));
      if (!ok) {
        return;
      }
      try {
        await this.$store.dispatch(`organizationControl/delete`, id);
        this.paginateControl();
        this.$toast.success(this.$t("common.delete-successful"));
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@modules/core/compliance-item/organization-policy.scss";
</style>
